import type { RowData } from "@tanstack/react-table";
import type { View } from "@common/types/views/views";
import type { SelectAllMatchingControl } from "../ViewTableCheckboxHeader";
import type { ViewTableInstance } from "./table";

interface PageRowSelection {
    isAllPageRowsSelected: boolean;
    isSomePageRowsSelected: boolean;
}

/** Whether the selection column header shows the select-all checkbox. */
export const shouldRenderSelectAll = (viewOptions: View["options"]): boolean =>
    viewOptions?.bulkActions === true && viewOptions.hideSelectAll !== true;

/**
 * Loaded page-row selection for the select-all header, read only when the
 * header renders and no all-matching control drives it.
 */
export const getPageRowSelection = <TData extends RowData>({
    table,
    viewOptions,
    selectAllControl,
}: {
    table: ViewTableInstance<TData>;
    viewOptions: View["options"];
    selectAllControl: SelectAllMatchingControl | undefined;
}): PageRowSelection => {
    const shouldReadPageRowSelection =
        shouldRenderSelectAll(viewOptions) && !selectAllControl;
    return {
        isAllPageRowsSelected:
            shouldReadPageRowSelection && table.getIsAllPageRowsSelected(),
        isSomePageRowsSelected:
            shouldReadPageRowSelection && table.getIsSomePageRowsSelected(),
    };
};

/** The select-all checkbox state: the all-matching control when given, else the loaded page rows. */
export const getSelectAllCheckboxState = ({
    control,
    isAllPageRowsSelected,
    isSomePageRowsSelected,
}: PageRowSelection & {
    control: SelectAllMatchingControl | undefined;
}): { isChecked: boolean; isPartiallyChecked: boolean } =>
    control
        ? {
              isChecked: control.isChecked,
              isPartiallyChecked: control.isIndeterminate,
          }
        : {
              isChecked: isAllPageRowsSelected,
              isPartiallyChecked:
                  isSomePageRowsSelected && !isAllPageRowsSelected,
          };
